import { api } from './api'
import { LabResult, LabResultFilters, LabResultSummary } from '../types/labResult'

export const labResultService = {
  /**
   * Get all lab results with optional filters
   */
  getAll: async (filters?: LabResultFilters): Promise<LabResult[]> => {
    const response = await api.get<LabResult[]>('/lab-results', {
      params: filters,
    })
    return response.data
  },

  /**
   * Get lab result summaries (for list views)
   */
  getSummaries: async (filters?: LabResultFilters): Promise<LabResultSummary[]> => {
    const response = await api.get<LabResultSummary[]>('/lab-results/summaries', {
      params: filters,
    })
    return response.data
  },

  /**
   * Get lab result by ID
   */
  getById: async (id: string): Promise<LabResult> => {
    const response = await api.get<LabResult>(`/lab-results/${id}`)
    return response.data
  },

  /**
   * Get lab results for a specific patient
   */
  getByPatient: async (patientId: string): Promise<LabResult[]> => {
    const response = await api.get<LabResult[]>(`/lab-results`, {
      params: { patientId },
    })
    return response.data
  },

  /**
   * Sync lab results from Healthlink
   */
  syncFromHealthlink: async (patientId?: string): Promise<{ count: number; results: LabResult[] }> => {
    const response = await api.post<{ count: number; results: LabResult[] }>('/lab-results/sync-healthlink', {
      patientId,
    })
    return response.data
  },

  /**
   * Create a manual lab result entry
   */
  create: async (data: Partial<LabResult>): Promise<LabResult> => {
    const response = await api.post<LabResult>('/lab-results', data)
    return response.data
  },

  /**
   * Update a lab result
   */
  update: async (id: string, data: Partial<LabResult>): Promise<LabResult> => {
    const response = await api.put<LabResult>(`/lab-results/${id}`, data)
    return response.data
  },

  /**
   * Delete a lab result
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/lab-results/${id}`)
  },

  /**
   * Mark a lab result as reviewed (complete)
   */
  markAsReviewed: async (id: string): Promise<LabResult> => {
    const response = await api.post<LabResult>(`/lab-results/${id}/review`)
    return response.data
  },

  /**
   * Mark multiple lab results as reviewed (complete)
   */
  markMultipleAsReviewed: async (ids: string[]): Promise<LabResult[]> => {
    const response = await api.post<LabResult[]>('/lab-results/review-multiple', { ids })
    return response.data
  },
}

